import React, { useState } from 'react';
import { BiX, BiUser, BiPhone, BiMapPin } from 'react-icons/bi';
import { BsBicycle } from 'react-icons/bs';
import { Order } from '../../types/orders';
import toast from 'react-hot-toast';

interface Rider {
  id: string;
  name: string;
  phone: string;
  location: string;
  rating: number;
  totalOrders: number;
  distanceToPickup: string;
  vehicleType: 'bicycle' | 'motorcycle';
  availability: 'Internal' | 'External';
}

interface ReassignOrderModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (riderId: string, reason: string) => void;
}

const ReassignOrderModal: React.FC<ReassignOrderModalProps> = ({
  order,
  isOpen,
  onClose,
  onConfirm
}) => {
  const [selectedRider, setSelectedRider] = useState<Rider | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Mock data for available riders
  const availableRiders: Rider[] = [
    {
      id: '1',
      name: 'Emanuel Wilson',
      phone: '+234 567 867 9089',
      location: 'Gbagada Area, Mainland',
      rating: 4.5,
      totalOrders: 123,
      distanceToPickup: '1.5km',
      vehicleType: 'bicycle',
      availability: 'Internal'
    },
    {
      id: '2',
      name: 'Emanuel Wilson',
      phone: '+234 567 867 9089',
      location: 'Gbagada Area, Mainland',
      rating: 4.5,
      totalOrders: 123,
      distanceToPickup: '1.5km',
      vehicleType: 'bicycle',
      availability: 'External'
    }
  ];

  const handleConfirmReassignment = async () => {
    if (!selectedRider) {
      toast.error('Please select a rider');
      return;
    }
    
    if (!reason.trim()) {
      toast.error('Please enter a reason for reassignment');
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(selectedRider.id, reason);
      toast.success('Order successfully reassigned');
      onClose();
      setSelectedRider(null);
      setReason('');
    } catch (error) {
      toast.error('Failed to reassign order');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedRider(null);
    setReason('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-teal-600">Reassign Order</h2>
              <p className="text-sm text-gray-600 mt-1">
                Reassign order {order.orderNumber} to a different rider. Current rider: {order.rider?.name || 'N/A'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <BiX className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          <div className="p-6">
            {/* Order Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order {order.orderNumber}</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Customer Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Customer Information:</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <BiUser className="w-4 h-4 text-gray-400" />
                        <span>{order.customer.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BiPhone className="w-4 h-4 text-gray-400" />
                        <span>{order.customer.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BiMapPin className="w-4 h-4 text-gray-400" />
                        <span>{order.customer.address}</span>
                      </div>
                    </div>
                  </div>

                  {/* Restaurant Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Restaurant Information:</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <BiUser className="w-4 h-4 text-gray-400" />
                        <span>{order.restaurant.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BiPhone className="w-4 h-4 text-gray-400" />
                        <span>{order.restaurant.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BiMapPin className="w-4 h-4 text-gray-400" />
                        <span>{order.restaurant.address}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      En-route
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Select New Rider */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Select a New Rider ({availableRiders.length})
              </h3>
              <div className="space-y-3">
                {availableRiders.map((rider) => (
                  <div
                    key={rider.id}
                    className={`relative p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedRider?.id === rider.id
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedRider(rider)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <BiUser className="w-6 h-6 text-gray-600" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-gray-900">{rider.name}</h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            rider.availability === 'Internal' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {rider.availability}
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>{rider.location}</p>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <span className="text-yellow-500">★</span>
                              <span>{rider.rating}</span>
                              <BsBicycle className="w-3 h-3 ml-2" />
                              <span>Bicycle</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">Distance to Pickup</div>
                        <div className="text-lg font-bold text-gray-900">{rider.distanceToPickup}</div>
                        <div className="text-sm text-gray-600">{rider.totalOrders} Orders</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reason for Reassignment */}
            <div className="mb-6">
              <label htmlFor="reason" className="block text-lg font-semibold text-gray-900 mb-2">
                Reason for reassignment
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter the reason for reassigning this order....."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel Reassignment
              </button>
              <button
                onClick={handleConfirmReassignment}
                disabled={isLoading || !selectedRider || !reason.trim()}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Processing...' : 'Confirm Reassignment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReassignOrderModal;